import { useMemo, useState, useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import { BiLoaderAlt, BiMailSend } from "react-icons/bi";
import Modal from "../../../components/modal";
import { useModal } from "../../../customHooks/useModal";
import { useLazyGetPendingInvitesQuery } from "../../../store/pendingInvitesApi";

const LOADING_DURATION_SECONDS = 10;

// LocalStorage keys
const STORAGE_KEYS = {
  COUNTDOWN: "inviteCountdown",
  TOAST_ID: "inviteToastId",
  START_TIME: "inviteStartTime",
} as const;

// Toast IDs
const TOAST_IDS = {
  SUCCESS: "invites-sent-success",
} as const;

// API responses
const API_RESPONSES = {
  NO_USERS_FOUND: "There are no pending shipments",
} as const;

// Messages
const MESSAGES = {
  ALL_INVITES_SENT: "There are no users to send invites to",
  SENDING_INVITES: "Sending invites...",
  INVITES_SENT: "Invites sent successfully",
} as const;

const SendInviteButton = () => {
  const [isSending, setIsSending] = useState(false);
  const { isShown: isModalShown, toggle: toggleModal } = useModal();
  const [
    getPendingInvites,
    { data: pendingInvites, isFetching: isLoading, error },
  ] = useLazyGetPendingInvitesQuery();

  // Check for existing countdown on component mount
  useEffect(() => {
    const savedCountdown = localStorage.getItem(STORAGE_KEYS.COUNTDOWN);
    const savedToastId = localStorage.getItem(STORAGE_KEYS.TOAST_ID);
    const savedStartTime = localStorage.getItem(STORAGE_KEYS.START_TIME);

    if (savedCountdown && savedToastId && savedStartTime) {
      const startTime = parseInt(savedStartTime);
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, LOADING_DURATION_SECONDS - elapsed);

      if (remaining > 0) {
        setIsSending(true);

        // Continue the countdown
        const interval = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          const remaining = Math.max(0, LOADING_DURATION_SECONDS - elapsed);

          if (remaining <= 0) {
            clearInterval(interval);
            setIsSending(false);
            localStorage.removeItem(STORAGE_KEYS.COUNTDOWN);
            localStorage.removeItem(STORAGE_KEYS.TOAST_ID);
            localStorage.removeItem(STORAGE_KEYS.START_TIME);
            toast.dismiss(savedToastId);
            toast.success(MESSAGES.INVITES_SENT, {
              id: TOAST_IDS.SUCCESS,
            });
          } else {
            localStorage.setItem(STORAGE_KEYS.COUNTDOWN, remaining.toString());
          }
        }, 1000);

        return () => clearInterval(interval);
      } else {
        // Countdown finished, clean up without showing success message
        // because the process wasn't completed in this session
        localStorage.removeItem(STORAGE_KEYS.COUNTDOWN);
        localStorage.removeItem(STORAGE_KEYS.TOAST_ID);
        localStorage.removeItem(STORAGE_KEYS.START_TIME);
        if (savedToastId) {
          toast.dismiss(savedToastId);
        }
      }
    }
  }, []);

  const sendInvite = async () => {
    toggleModal();
    setIsSending(true);
    const res = await fetch(`https://purls.freeway.com/api/leads/reset`);
    const result = await res.text();
    console.log(result);
    if (result === API_RESPONSES.NO_USERS_FOUND) {
      toast.success(MESSAGES.ALL_INVITES_SENT);
      setIsSending(false);
      return;
    }

    const modal = toast.loading(MESSAGES.SENDING_INVITES);
    const startTime = Date.now();

    // Save to localStorage
    localStorage.setItem(
      STORAGE_KEYS.COUNTDOWN,
      LOADING_DURATION_SECONDS.toString(),
    );
    localStorage.setItem(STORAGE_KEYS.TOAST_ID, modal);
    localStorage.setItem(STORAGE_KEYS.START_TIME, startTime.toString());

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, LOADING_DURATION_SECONDS - elapsed);

      if (remaining <= 0) {
        clearInterval(interval);
        setIsSending(false);
        localStorage.removeItem(STORAGE_KEYS.COUNTDOWN);
        localStorage.removeItem(STORAGE_KEYS.TOAST_ID);
        localStorage.removeItem(STORAGE_KEYS.START_TIME);
        toast.dismiss(modal);
        toast.success(MESSAGES.INVITES_SENT, {
          id: TOAST_IDS.SUCCESS,
        });
      } else {
        localStorage.setItem(STORAGE_KEYS.COUNTDOWN, remaining.toString());
      }
    }, 1000);
  };

  return (
    <>
      <Toaster position="top-center" />
      <Modal
        isActive={isModalShown}
        clouse={toggleModal}
        title="Pending Invitations"
      >
        <div
          style={{ maxHeight: "500px", overflowY: "auto", overflowX: "auto" }}
        >
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              Loading...
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
              Error loading pending invitations
            </div>
          ) : !pendingInvites || pendingInvites.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              No pending invitations
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#f5f5f5",
                    position: "sticky",
                    top: 0,
                  }}
                >
                  <th
                    style={{
                      padding: "12px",
                      border: "1px solid #ddd",
                      fontWeight: 600,
                    }}
                  >
                    #
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      border: "1px solid #ddd",
                      fontWeight: 600,
                    }}
                  >
                    Email
                  </th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((invite, index) => (
                  <tr key={index} style={{ borderBottom: "1px solid #ddd" }}>
                    <td
                      style={{
                        padding: "12px",
                        border: "1px solid #ddd",
                        width: "50px",
                        textAlign: "center",
                      }}
                    >
                      {index + 1}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        border: "1px solid #ddd",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {invite.email}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className="c-table__button-secondary"
            onClick={toggleModal}
          >
            Cancel
          </button>
          <button
            type="button"
            className="c-table__button-primary"
            onClick={sendInvite}
            disabled={
              isSending || !pendingInvites || pendingInvites.length === 0
            }
          >
            {isSending ? "Sending..." : "Send Invites"}
          </button>
        </div>
      </Modal>
      <button
        className={`c-table__button-secondary ${isSending ? "c-table__button-secondary--disabled" : ""
          }`}
        onClick={() => {
          getPendingInvites();
          toggleModal();
        }}
        disabled={isSending}
      >
        {isSending ? (
          <>
            <BiLoaderAlt
              style={{
                width: "20px",
                height: "20px",
                animation: "spin 1s linear infinite",
              }}
              size={40}
            />
            Sending...
          </>
        ) : (
          <>
            <BiMailSend
              style={{
                width: "20px",
                height: "20px",
              }}
              size={40}
              width={40}
              height={40}
            />
            Send Invites
          </>
        )}
      </button>
    </>
  );
};

export default SendInviteButton;
